/**
 * PostHogClass
 *
 * Thin wrapper around the global PostHog snippet that is initialised in the page
 * layouts (templates/Layout/website_layout.php for the public site). It exposes
 * safe identify/capture helpers that quietly no-op when PostHog is unavailable
 * (blocked by an ad-blocker, not yet loaded, etc.) so callers never need to guard.
 *
 * PostHog is configured with `person_profiles: 'identified_only'`, so a person
 * profile is only created once identify() runs. We therefore call identify() on
 * the known-user touch points (form submits + cg_token).
 *
 * All methods are static — there is nothing to instantiate. Use:
 *   PostHogClass.identify(email);
 *   PostHogClass.capture('event_name', { ...props });
 */
export default class PostHogClass {
    /**
     * @returns {boolean} true when the PostHog library is loaded and ready.
     */
    static isReady() {
        return typeof window !== 'undefined'
            && !!window.posthog
            && typeof window.posthog.identify === 'function';
    }

    /**
     * Identify a known user by email (used as the PostHog distinct id).
     * @param {string} email - The user's email address.
     * @param {Object} [properties={}] - Extra person properties to set.
     * @returns {boolean} true if the identify call was dispatched.
     */
    static identify(email, properties = {}) {
        const normalized = (email || '').trim();
        if (!normalized || !PostHogClass.isReady()) {
            return false;
        }

        try {
            window.posthog.identify(normalized, { email: normalized, ...properties });
            return true;
        } catch (error) {
            console.error('PostHog identify failed:', error);
            return false;
        }
    }

    /**
     * Extract user properties from a submitted form's FormData for use as
     * event properties on a capture() call. Reads the standard website form
     * fields (email, customer_name, job_title); the single `customer_name`
     * field is split into first/last name. The contact form also carries
     * `contact_message` and `phone_number`; these are included when present so
     * other forms that lack them are unaffected. Only present fields are added.
     *
     * @param {FormData} formData - The submitted form's data.
     * @returns {Object} A flat map of user properties (may be empty).
     */
    static userPropsFromFormData(formData) {
        if (!formData || typeof formData.get !== 'function') {
            return {};
        }

        const email = (formData.get('email') || '').trim();
        const fullName = (formData.get('customer_name') || '').trim();
        const jobTitle = (formData.get('job_title') || '').trim();
        const message = (formData.get('contact_message') || '').trim();
        const phone = (formData.get('phone_number') || '').trim();

        const props = {};

        if (email) {
            props.email = email;
        }

        if (fullName) {
            const [firstName, ...rest] = fullName.split(/\s+/);
            props.name = fullName;
            props.first_name = firstName;
            if (rest.length) {
                props.last_name = rest.join(' ');
            }
        }

        if (jobTitle) {
            props.job_title = jobTitle;
        }

        if (message) {
            props.message = message;
        }

        if (phone) {
            props.phone = phone;
        }

        return props;
    }

    /**
     * Capture a custom event.
     *
     * Pass `{ transport: 'sendBeacon' }` in options for events fired immediately
     * before a redirect (e.g. form submits): the default batched request would be
     * cancelled when the page navigates away, whereas sendBeacon still delivers —
     * important when a downstream PostHog webhook (n8n) triggers off the event.
     *
     * @param {string} event - The event name.
     * @param {Object} [properties={}] - Event properties.
     * @param {Object} [options={}] - PostHog capture options (e.g. transport).
     * @returns {boolean} true if the capture call was dispatched.
     */
    static capture(event, properties = {}, options = {}) {
        if (!event || !PostHogClass.isReady() || typeof window.posthog.capture !== 'function') {
            return false;
        }

        try {
            window.posthog.capture(event, properties, options);
            return true;
        } catch (error) {
            console.error('PostHog capture failed:', error);
            return false;
        }
    }
}
